import { buildSleeperPickKey, getKtcValueFields, parseKtcPickLabel } from './keepTradeCut.js';

const SLEEPER = 'https://api.sleeper.app/v1';

const fetchJson = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Sleeper request failed (${response.status}). Check the league ID and try again.`);
  }
  return response.json();
};

const formatPlayerName = (p) => {
  if (!p) return 'Unknown player';
  return (
    p.full_name ||
    [p.first_name, p.last_name].filter(Boolean).join(' ') ||
    String(p.player_id || '')
  );
};

const positionLabel = (p) =>
  p?.position || p?.fantasy_positions?.[0] || p?.depth_chart_position || '—';

const teamLabel = (p) => p?.team || p?.metadata?.team_abbr || 'FA';

const normalizeNameKey = (name) =>
  (name || '')
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/jr\.?|sr\.?|iii|ii|iv/gi, '')
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Sleeper and KeepTradeCut use different team abbreviations (e.g. KC vs KCC).
 * We only use team as a tie-breaker when multiple players share a name.
 */
const teamRoughMatch = (sleeperTeam, ktcTeam) => {
  if (!sleeperTeam || !ktcTeam || sleeperTeam === 'FA') return true;
  const a = sleeperTeam.toUpperCase();
  const b = ktcTeam.toUpperCase();
  if (a === b) return true;
  if (a === 'KC' && b === 'KCC') return true;
  if (a === 'WAS' && b === 'WSH') return true;
  if (a === 'OAK' && b === 'LVR') return true;
  return false;
};

const positionsMatch = (sleeperPos, ktcPos) => {
  if (!sleeperPos || !ktcPos) return false;
  const s = String(sleeperPos).toUpperCase();
  const k = String(ktcPos).toUpperCase();
  if (s === k) return true;
  if ((s === 'DEF' || s === 'DST') && (k === 'DEF' || k === 'DST')) return true;
  return false;
};

/**
 * Link a Sleeper player row to KTC by name + position (+ team when ambiguous).
 * KTC player ids are internal; public pages expose `mflid`, which does not match Sleeper ids.
 */
const findKtcRowForSleeperPlayer = (sp, ktcByPlayerId) => {
  const wantName = normalizeNameKey(formatPlayerName(sp));
  const wantPos = positionLabel(sp);
  const wantTeam = teamLabel(sp);

  const candidates = [];
  for (const row of ktcByPlayerId.values()) {
    if (row?.position === 'RDP') continue;
    if (!positionsMatch(wantPos, row.position)) continue;
    const rowName = normalizeNameKey(row.playerName);
    if (rowName !== wantName) continue;
    if (!teamRoughMatch(wantTeam, row.team)) continue;
    candidates.push(row);
  }

  if (candidates.length === 1) return candidates[0];
  if (candidates.length > 1) {
    const withTeam = candidates.filter((c) => teamRoughMatch(wantTeam, c.team));
    if (withTeam.length === 1) return withTeam[0];
  }
  return null;
};

/**
 * Load Sleeper NFL players once per session (large payload).
 */
let sleeperPlayersCache = null;
export const getSleeperPlayerMap = async () => {
  if (sleeperPlayersCache) return sleeperPlayersCache;
  sleeperPlayersCache = await fetchJson(`${SLEEPER}/players/nfl`);
  return sleeperPlayersCache;
};

/**
 * Fetch league, rosters, users, traded picks, and merge with KTC rows.
 */
export const buildSleeperKtcRosterView = async ({
  leagueId,
  rosterId,
  ktcByPlayerId,
  format,
}) => {
  if (!leagueId?.trim()) {
    throw new Error('Enter a Sleeper league ID.');
  }
  const lid = leagueId.trim();

  const [league, users, rosters, tradedPicks, sleeperPlayers] = await Promise.all([
    fetchJson(`${SLEEPER}/league/${lid}`),
    fetchJson(`${SLEEPER}/league/${lid}/users`),
    fetchJson(`${SLEEPER}/league/${lid}/rosters`),
    fetchJson(`${SLEEPER}/league/${lid}/traded_picks`),
    getSleeperPlayerMap(),
  ]);

  if (!Array.isArray(rosters) || !rosters.length) {
    throw new Error('No rosters returned for this league.');
  }

  const leagueSize = Number(league?.total_rosters) || rosters.length;

  const userById = new Map((users || []).map((u) => [u.user_id, u]));

  const teams = rosters.map((r) => {
    const owner = r?.owner_id ? userById.get(r.owner_id) : null;
    const teamName =
      owner?.metadata?.team_name ||
      owner?.display_name ||
      owner?.username ||
      (r?.roster_id != null ? `Team ${r.roster_id}` : 'Team');

    return {
      rosterId: r.roster_id,
      teamName,
      ownerId: r.owner_id || null,
    };
  });

  const selected = teams.find((t) => String(t.rosterId) === String(rosterId));
  if (!selected) {
    throw new Error('Pick a team from this league.');
  }

  const roster = rosters.find((r) => String(r.roster_id) === String(rosterId));
  const playerIds = new Set();

  const addIds = (list) => {
    if (!Array.isArray(list)) return;
    list.forEach((id) => {
      if (id && id !== '0') playerIds.add(String(id));
    });
  };

  addIds(roster?.players);
  addIds(roster?.reserve);
  addIds(roster?.taxi);
  addIds(roster?.starters);

  const playerRows = [];

  for (const pid of playerIds) {
    const sp = sleeperPlayers[pid];
    const name = formatPlayerName(sp);
    const pos = positionLabel(sp);
    const team = teamLabel(sp);

    const ktcRow = findKtcRowForSleeperPlayer(sp, ktcByPlayerId);
    const vals = ktcRow ? getKtcValueFields(ktcRow, format) : { value: null, rank: null, positionalRank: null };

    playerRows.push({
      kind: 'player',
      sleeperId: pid,
      name,
      position: pos,
      team,
      ktcValue: vals.value,
      ktcRank: vals.rank,
      ktcPosRank: vals.positionalRank,
      matchKind: ktcRow ? 'name' : 'none',
      ktcName: ktcRow?.playerName || null,
    });
  }

  const ownedPicks = (tradedPicks || []).filter((tp) => Number(tp.owner_id) === Number(rosterId));

  const pickRows = ownedPicks.map((tp) => {
    const seasonNum = Number(tp.season);
    const roundNum = Number(tp.round);
    const pickKey = buildSleeperPickKey({
      season: seasonNum,
      round: roundNum,
      rosterId: tp.roster_id,
      leagueSize,
    });

    let ktcRow = null;
    if (pickKey) {
      for (const row of ktcByPlayerId.values()) {
        if (row?.position !== 'RDP') continue;
        const parsed = parseKtcPickLabel(row.playerName);
        if (parsed?.key === pickKey) {
          ktcRow = row;
          break;
        }
      }
    }

    const vals = ktcRow ? getKtcValueFields(ktcRow, format) : { value: null, rank: null, positionalRank: null };

    const originalSlot = tp.roster_id;

    return {
      kind: 'pick',
      sleeperId: `pick-${tp.season}-${tp.round}-${tp.roster_id}-${tp.owner_id}`,
      name: `${tp.season} round ${tp.round} (original roster ${originalSlot})`,
      position: 'PICK',
      team: '—',
      ktcValue: vals.value,
      ktcRank: vals.rank,
      ktcPosRank: vals.positionalRank,
      matchKind: ktcRow ? 'pick_label' : 'none',
      ktcName: ktcRow?.playerName || null,
      note:
        roundNum > 3
          ? 'KTC lists rookie draft picks through round 3 on the main dynasty board.'
          : null,
    };
  });

  const disclaimer =
    'Values come from KeepTradeCut’s public dynasty pages (same numbers as on keeptradecut.com). We match NFL players by name and position; verify unusual cases. Kickers and defenses are not included on that combined board. Draft picks are mapped to Early / Mid / Late using each pick’s original owner roster slot vs league size—adjust mentally if your league orders picks differently.';

  return {
    leagueName: league?.name || 'Sleeper league',
    season: String(league?.season || new Date().getUTCFullYear()),
    leagueSize,
    teams,
    selectedRosterId: rosterId,
    players: playerRows.sort((a, b) => (b.ktcValue ?? -1) - (a.ktcValue ?? -1)),
    picks: pickRows.sort((a, b) => (b.ktcValue ?? -1) - (a.ktcValue ?? -1)),
    disclaimer,
  };
};
